import { baseApi } from '@/app/baseApi';

export interface ThreadMessage {
  id: string;
  from: string;
  body: string;
  sentAt: string;
  readByMe: boolean;
}

export interface ThreadSummary {
  id: string;
  subject: string;
  members: string[];
  audience: ('manager' | 'hr')[];
  status: 'Open' | 'Closed';
  lastMessageAt: string;
  messages: ThreadMessage[];
  unread: number;
}

export interface NotificationSummary {
  id: string;
  title: string;
  body: string;
  icon: string;
  link: string;
  read: boolean;
  createdAt: string;
}

export const messagingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    threads: builder.query<{ threads: ThreadSummary[]; total: number }, void>({
      query: () => '/messaging/threads',
      providesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    startThread: builder.mutation<
      { thread: ThreadSummary },
      { subject: string; body: string; audience: ('manager' | 'hr')[] }
    >({
      query: (body) => ({ url: '/messaging/threads', method: 'POST', body }),
      invalidatesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    replyToThread: builder.mutation<{ thread: ThreadSummary }, { id: string; body: string }>({
      query: ({ id, body }) => ({ url: `/messaging/threads/${id}/messages`, method: 'POST', body: { body } }),
      invalidatesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    markThreadRead: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/messaging/threads/${id}/read`, method: 'POST' }),
      invalidatesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    notifications: builder.query<
      { notifications: NotificationSummary[]; total: number; unread: number },
      { unreadOnly?: 'true' } | void
    >({
      query: (args) => ({ url: '/messaging/notifications', params: args ?? {} }),
      providesTags: [{ type: 'Notification', id: 'LIST' }],
    }),

    markNotificationRead: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/messaging/notifications/${id}/read`, method: 'POST' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),

    raiseNotification: builder.mutation<
      unknown,
      { recipient: string; title: string; body?: string; link?: string }
    >({
      query: (body) => ({ url: '/messaging/notifications', method: 'POST', body }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),

    markAllNotificationsRead: builder.mutation<{ marked: number }, void>({
      query: () => ({ url: '/messaging/notifications/read-all', method: 'POST' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
  }),
});

export const {
  useRaiseNotificationMutation,
  useThreadsQuery,
  useStartThreadMutation,
  useReplyToThreadMutation,
  useMarkThreadReadMutation,
  useNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} = messagingApi;
